"use client"

import { zodResolver } from "@hookform/resolvers/zod"
import { ExternalLink } from "lucide-react"
import { Link } from "next-view-transitions"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

import { useCreateTspPublicMessage } from "@/lib/apps/tsp/hooks/use-public-message-mutations"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"

const FormSchema = z.object({
  email: z.string().email(),
  nb_athletes: z.coerce.number().int().min(0).default(0),
  nb_coaches: z.coerce.number().int().min(0).default(0),
  message: z.string().min(1),
})

export default function MessageDialogLanding() {
  const [open, setOpen] = useState(false)
  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      email: "",
      nb_athletes: 0,
      nb_coaches: 0,
      message: "",
    },
  })

  const { mutateAsync } = useCreateTspPublicMessage({
    onSuccess: () => {
      toast.success("Message sent. We'll get back to you shortly.")
      form.reset()
      setOpen(false)
    },
  })

  async function onSubmit(values: z.infer<typeof FormSchema>) {
    try {
      await mutateAsync(values)
    } catch {
      // error toast handled by mutation default
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="xl"
          variant="outline"
          className="text-md border-foreground group block bg-transparent hover:bg-transparent"
        >
          Book a demo
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Contact the Twister Spread team</DialogTitle>
          <DialogDescription>
            Let us know what you need and we&apos;ll get back to you as soon as
            possible.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="example@gmail.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="nb_athletes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Number of athletes</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      placeholder="How many athletes do you have?"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="nb_coaches"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Number of coaches</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      placeholder="How many coaches do you have?"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Type your message here."
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full">
              Submit
            </Button>
          </form>
        </Form>
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <Separator className="w-full" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background text-muted-foreground px-2">or</span>
          </div>
        </div>

        <Link href="https://cal.com/tom-jeannesson/en">
          <Button
            type="button"
            variant="outline"
            className="flex w-full items-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              height="24"
              viewBox="0 0 24 24"
              width="24"
            >
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
              <path d="M1 1h22v22H1z" fill="none" />
            </svg>
            Book a call <ExternalLink className="size-4" />
          </Button>
        </Link>
      </DialogContent>
    </Dialog>
  )
}
